// frontend/src/components/bossBattles/LoadoutSetup.js
// Component for setting up participant loadout (player + gear selection)

import React, { useState } from 'react';
import './LoadoutSetup.css';

/**
 * LoadoutSetup - Configure player and gear for boss battle
 * 
 * @param {Object} participant - Current participant data
 * @param {Array} availablePlayers - Players available for selection
 * @param {Array} availableGear - Gear items available with pricing
 * @param {Function} onSaveLoadout - Callback to save loadout configuration
 * @param {Function} onConfirmLoadout - Callback to confirm loadout is ready
 * @param {boolean} isLocked - Whether the league is locked (no changes allowed)
 */
function LoadoutSetup({ 
    participant, 
    availablePlayers = [], 
    availableGear = [], 
    onSaveLoadout, 
    onConfirmLoadout,
    isLocked = false
}) {
    // VALIDATE REQUIRED PROPS
    if (!participant) {
        throw new Error('❌ LoadoutSetup: participant prop is required');
    }
    
    const [selectedPlayer, setSelectedPlayer] = useState(participant.player_id || null);
    const [selectedGear, setSelectedGear] = useState(participant.gear_ids || []);
    const [saving, setSaving] = useState(false);
    const [confirming, setConfirming] = useState(false);
    
    const MAX_GEAR_SLOTS = 3;
    
    // Calculate total mythril cost
    const calculateMythrilCost = () => {
        let total = 0;
        selectedGear.forEach(gearId => {
            const gear = availableGear.find(g => g.gear_id === gearId);
            if (gear) {
                total += gear.mythril_cost || 0;
            }
        });
        return total;
    };
    
    const mythrilSpent = calculateMythrilCost();
    
    const handlePlayerSelect = (playerId) => {
        if (isLocked) return;
        setSelectedPlayer(playerId);
    };
    
    const handleGearToggle = (gearId) => {
        if (isLocked) return;
        
        setSelectedGear(prev => {
            if (prev.includes(gearId)) {
                // Remove gear
                return prev.filter(id => id !== gearId);
            } else {
                // Add gear if under limit
                if (prev.length >= MAX_GEAR_SLOTS) {
                    alert(`You can only equip ${MAX_GEAR_SLOTS} gear items`);
                    return prev;
                }
                return [...prev, gearId];
            }
        });
    };
    
    const handleSaveLoadout = async () => {
        if (!selectedPlayer) {
            alert('Please select a player first');
            return;
        }
        
        if (!onSaveLoadout) {
            console.error('❌ LoadoutSetup: onSaveLoadout callback not provided');
            return;
        }
        
        try {
            setSaving(true);
            
            await onSaveLoadout({
                player_id: selectedPlayer,
                gear_ids: selectedGear,
                mythril_spent: mythrilSpent
            });
            
        } catch (error) {
            console.error('❌ LoadoutSetup: Error saving loadout:', error);
            alert(`Failed to save loadout: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };
    
    const handleConfirmLoadout = async () => {
        if (!participant.player_id) {
            alert('Please save your loadout first');
            return;
        }
        
        if (!onConfirmLoadout) {
            console.error('❌ LoadoutSetup: onConfirmLoadout callback not provided');
            return;
        }
        
        try {
            setConfirming(true);
            await onConfirmLoadout();
        } catch (error) {
            console.error('❌ LoadoutSetup: Error confirming loadout:', error);
            alert(`Failed to confirm loadout: ${error.message}`);
        } finally {
            setConfirming(false);
        }
    };
    
    const isLoadoutChanged = selectedPlayer !== participant.player_id || 
                            JSON.stringify(selectedGear.sort()) !== JSON.stringify((participant.gear_ids || []).sort());
    
    return (
        <div className="loadout-setup">
            {/* Role Display */}
            <div className="role-display">
                <h3 className="role-title">Your Role</h3>
                <div className={`role-badge ${participant.role?.toLowerCase()}`}>
                    <span className="role-icon">
                        {participant.role === 'STRIKER' ? '⚔️' : 
                         participant.role === 'PROTECTOR' ? '🛡️' : '✨'}
                    </span>
                    <span className="role-name">{participant.role}</span>
                </div>
            </div>
            
            {/* Player Selection */}
            <div className="player-selection-section">
                <h3 className="section-title">
                    Select Your Champion
                    {selectedPlayer && <span className="selected-indicator">✓</span>}
                </h3>
                
                {availablePlayers.length === 0 ? (
                    <div className="empty-message">
                        No players available. Please check your roster.
                    </div>
                ) : (
                    <div className="player-list">
                        {availablePlayers.map(player => (
                            <div
                                key={player.id}
                                className={`player-item ${selectedPlayer === player.id ? 'selected' : ''} ${isLocked ? 'locked' : ''}`}
                                onClick={() => handlePlayerSelect(player.id)}
                            >
                                <div className="player-info">
                                    <span className="player-name">{player.name}</span>
                                    <span className="player-position">{player.position}</span>
                                </div>
                                {selectedPlayer === player.id && (
                                    <span className="check-icon">✓</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            
            {/* Gear Selection */}
            <div className="gear-selection-section">
                <h3 className="section-title">
                    Equip Gear ({selectedGear.length}/{MAX_GEAR_SLOTS})
                </h3>
                
                <div className="mythril-cost">
                    <span className="cost-label">Mythril Cost:</span>
                    <span className="cost-value">⚡ {mythrilSpent}</span>
                </div>
                
                {availableGear.length === 0 ? (
                    <div className="empty-message">
                        No gear available for purchase.
                    </div>
                ) : (
                    <div className="gear-list">
                        {availableGear.map(gear => {
                            const isSelected = selectedGear.includes(gear.gear_id);
                            const isDisabled = !isSelected && selectedGear.length >= MAX_GEAR_SLOTS;
                            
                            return (
                                <div
                                    key={gear.gear_id}
                                    className={`gear-item ${isSelected ? 'selected' : ''} ${isDisabled || isLocked ? 'disabled' : ''}`}
                                    onClick={() => !isDisabled && handleGearToggle(gear.gear_id)}
                                >
                                    <div className="gear-info">
                                        <span className="gear-name">{gear.gear_name}</span>
                                        <span className="gear-cost">⚡ {gear.mythril_cost}</span>
                                    </div>
                                    {isSelected && (
                                        <span className="check-icon">✓</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            
            {/* Loadout Status */}
            {participant.loadout_confirmed && (
                <div className="confirmed-banner">
                    ✓ Loadout Confirmed - Ready for Battle!
                </div>
            )}
            
            {/* Action Buttons */}
            {!isLocked && (
                <div className="loadout-actions">
                    {isLoadoutChanged && (
                        <button
                            className="save-button"
                            onClick={handleSaveLoadout}
                            disabled={saving || !selectedPlayer}
                        >
                            {saving ? 'Saving...' : 'Save Loadout'}
                        </button>
                    )}
                    
                    {!participant.loadout_confirmed && participant.player_id && (
                        <button
                            className="confirm-button"
                            onClick={handleConfirmLoadout}
                            disabled={confirming || isLoadoutChanged}
                        >
                            {confirming ? 'Confirming...' : 'Confirm Ready'}
                        </button>
                    )}
                    
                    {isLoadoutChanged && participant.player_id && (
                        <div className="warning-message">
                            ⚠️ Save your changes before confirming
                        </div>
                    )}
                </div>
            )}
            
            {isLocked && (
                <div className="locked-message">
                    🔒 Battle is locked - Loadout cannot be changed
                </div>
            )}
        </div>
    );
}

export default LoadoutSetup;
